/**
 * Per-persona tour scripts (spec 23 §4.3). Selectors are kept loose
 * (sidebar `[href]` and Header `data-testid`) so the tours don't drift
 * into individual page internals. Each tour is 5-7 stops to fit a 90-
 * second walk-through without operator fatigue.
 *
 * A stop with ``selector: null`` renders centered with no spotlight —
 * used for the lead-in welcome card and any "this lives on a different
 * page" explainer.
 */
import type { OnboardingPersona } from "@/api/onboarding";
import type { TourStop } from "./driver-config";

export const PERSONA_TOURS: Record<OnboardingPersona, TourStop[]> = {
  superadmin: [
    {
      selector: null,
      title: "Welcome, Super Admin.",
      description:
        "You see every client and every site. This 90-second tour walks you through the rooms you'll spend the most time in.",
    },
    {
      selector: 'a[href="/admin/clients"]',
      title: "Clients",
      description:
        "The full list of tenants on the platform. Open one to act as their team and see what they see.",
    },
    {
      selector:
        '[data-testid="tenant-switcher"], [data-testid="impersonation-banner"]',
      title: "Impersonation",
      description:
        "Pick a client from the switcher up top to view as their admin. A red banner stays pinned so you never forget you're acting on their behalf.",
      side: "bottom",
    },
    {
      selector: 'a[href="/ml"]',
      title: "MLOps",
      description:
        "Forecast accuracy, model registry, and retrain controls. Promote, retire, retrain — all gated behind audit.",
    },
    {
      selector: 'a[href="/admin/platform"]',
      title: "Platform settings",
      description:
        "SLA windows, retention, escalation defaults — applied across every client unless they override.",
    },
    {
      selector: 'a[href="/admin/audit"]',
      title: "Audit log",
      description:
        "Every state change writes a row here, including denied attempts. Search by actor, target, or action.",
    },
    {
      selector: null,
      title: "You're set.",
      description:
        "You can replay this tour any time from Getting Started.",
    },
  ],
  clientadmin: [
    {
      selector: null,
      title: "Welcome — let's set up your fleet.",
      description:
        "Add customers, register sites, invite your team, and you're live.",
    },
    {
      selector: 'a[href="/admin/customers"]',
      title: "Customers",
      description:
        "Customers are how you group sites + users for reports + access control. Add your first one here.",
    },
    {
      selector: 'a[href="/admin/sites"]',
      title: "Sites",
      description:
        "Connect each plant — live data from a re:bot device or your inverter cloud account.",
    },
    {
      selector: 'a[href="/admin/users"]',
      title: "Users",
      description:
        "Invite teammates by email; they get a one-time sign-in link. Roles + permissions are policy-based.",
    },
    {
      selector: 'a[href="/admin/clients"]',
      title: "Branding",
      description:
        "Logo, colours, and the display name your team sees in the app + emails. Editable per-client.",
    },
    {
      selector: 'a[href="/inbox"]',
      title: "Inbox",
      description:
        "Once sites are wired up, this is your daily landing page — every alarm, ticket, and SLA risk in one ordered list.",
    },
    {
      selector: null,
      title: "You're set.",
      description:
        "You can replay this tour any time from Getting Started.",
    },
  ],
  om: [
    {
      selector: null,
      title: "Welcome — your work queue is ready.",
      description:
        "Start your shift in Inbox: every open alarm, ticket, and SLA risk across your sites, ordered by impact.",
    },
    {
      selector: 'a[href="/inbox"]',
      title: "Inbox",
      description:
        "Top to bottom. Press E to acknowledge, A to assign, S to snooze. ? anywhere shows the shortcut sheet.",
    },
    {
      selector: 'a[href="/alarms"]',
      title: "Alarms",
      description:
        "Full alarm list with severity + status filters. Click any alarm for the recommended-action card.",
    },
    {
      selector: 'a[href="/tickets"]',
      title: "Tickets",
      description:
        "Maintenance work, escalations, and SLA-tracked tasks. Convert any alarm to a ticket with T.",
    },
    {
      selector: 'a[href="/map"]',
      title: "Map",
      description:
        "Every site plotted live. Press F for Focus Mode — an ops-room view that's perfect for a wall display.",
    },
    {
      selector: 'a[href="/reports"]',
      title: "Reports",
      description:
        "Generate a one-off report or schedule a recurring statement. Subscriptions auto-deliver as PDF.",
    },
    {
      selector: null,
      title: "You're set.",
      description:
        "Replay this tour from Getting Started any time. Press ? for the keyboard shortcut sheet.",
    },
  ],
  siteowner: [
    {
      selector: null,
      title: "Welcome to your dashboard.",
      description:
        "Here's how your plant is performing today and the bigger-picture impact you've made.",
    },
    {
      selector: 'a[href="/my-plants"]',
      title: "My Plants",
      description:
        "Today's energy, earnings, and CO₂ avoided across every site you own.",
    },
    {
      selector: null,
      title: "Live energy flow",
      description:
        "Each plant has a live diagram showing solar → battery → home → grid in real time — open any plant to see it.",
    },
    {
      selector: 'a[href="/reports"]',
      title: "Monthly statements",
      description:
        "Subscribe to a monthly PDF and we'll mail it the first of each month — energy, savings, and ESG impact.",
    },
    {
      selector: null,
      title: "You're set.",
      description: "Replay this tour any time from Getting Started.",
    },
  ],
};
